// What each of a set of traces (a Findings list's) ran: its agents, named as
// the fleet names them (lib/fleetConsole.js resolveAgent), and its models, for
// the list's search. The query is the trace-with-frames shape Fleet's agent
// reads use (lib/fleetConsoleReads.js), grouped by trace.
import { rangeToTimeRange, rowsToObjects, runQuery } from "./analyticsClient.js";
import { FLEET_RANGE, resolveAgent } from "./fleetConsole.js";
import { toApiRange } from "./shell.js";

// Traces per read, and groups a read returns at most.
const IN_CAP = 1000;
const GROUP_CAP = 10_000;

const F = (alias, entity) => (name) => ({ stepAlias: alias, field: `DIMENSION_FIELD_${entity}_${name}` });
const T = F("t", "CONVERSATION_TRACE");
const O = F("o", "CONVERSATION_SOURCE_OBSERVATION");
const dim = (field, outputAlias) => ({ field, outputAlias });
// A trace, with its frames joined LEFT so a trace with no frame still reads.
const ROOT = { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE", children: [{ edge: "EDGE_TYPE_CONVERSATION_TRACE_HAS_SOURCE_OBSERVATIONS", mode: "JOIN_MODE_LEFT", target: { alias: "o", entity: "ENTITY_TYPE_CONVERSATION_SOURCE_OBSERVATION" } }] };

export function traceNamesSpec({ range, ids }) {
    return {
        root: ROOT,
        timeRange: rangeToTimeRange(typeof range === "object" ? range : toApiRange(range)),
        output: {
            dimensions: [dim(T("TRACE_ID"), "trace"), dim(T("CLIENT_SURFACE"), "surface"), dim(T("PROVIDER"), "provider"), dim(O("AGENT_TYPE"), "agentType"), dim(O("MODEL"), "model")],
            metrics: [{ outputAlias: "sessions", computed: { field: T("TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
        filter: { pred: { field: T("TRACE_ID"), op: "OPERATOR_IN", value: ids } },
        limit: GROUP_CAP,
    };
}

/**
 * -> Promise<Map(trace id -> [name])>: each trace's agents and models. One read
 * per thousand traces; read once per set and range for the page's life, and a
 * failed read is asked again.
 */
const kept = new Map();
export function readTraceNames({ sessions = [], range = FLEET_RANGE } = {}, execute = runQuery) {
    const ids = [...new Set(sessions.filter(Boolean).map((id) => String(id).toLowerCase()))].sort();
    if (!ids.length) return Promise.resolve(new Map());
    const key = `${typeof range === "object" ? JSON.stringify(range) : range}|${ids.join(",")}`;
    if (!kept.has(key)) {
        const chunks = Array.from({ length: Math.ceil(ids.length / IN_CAP) }, (_, i) => ids.slice(i * IN_CAP, (i + 1) * IN_CAP));
        kept.set(key, Promise.all(chunks.map(async (chunk) => rowsToObjects(await execute(traceNamesSpec({ range, ids: chunk })))))
            .then((pages) => {
                const out = new Map();
                for (const row of pages.flat()) {
                    const id = String(row.trace || "").toLowerCase();
                    if (!id) continue;
                    if (!out.has(id)) out.set(id, new Set());
                    const agent = resolveAgent(row);
                    if (agent?.name) out.get(id).add(agent.name);
                    if (row.model) out.get(id).add(String(row.model));
                }
                return new Map([...out].map(([id, names]) => [id, [...names]]));
            })
            .catch((err) => { kept.delete(key); throw err; }));
        if (kept.size > 20) kept.delete(kept.keys().next().value);
    }
    return kept.get(key);
}
